import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Plus, Check, Circle, ChevronDown, ChevronRight,
  Calendar, Trash2, MoreHorizontal, X, Clock,
  AlertTriangle, ArrowUp, ArrowRight, ArrowDown,
  ListTodo, Pencil, Share2, Users
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

// ── Priority Config ──

const PRIORITY_CONFIG = {
  high: {
    label: 'High Priority',
    color: '#EF4444',
    bgLight: '#FEF2F2',
    borderLight: '#FECACA',
    icon: ArrowUp,
    badgeBg: '#FEE2E2',
    badgeText: '#991B1B',
  },
  medium: {
    label: 'Medium Priority',
    color: '#F59E0B',
    bgLight: '#FFFBEB',
    borderLight: '#FDE68A',
    icon: ArrowRight,
    badgeBg: '#FEF3C7',
    badgeText: '#92400E',
  },
  low: {
    label: 'Low Priority',
    color: '#3B82F6',
    bgLight: '#EFF6FF',
    borderLight: '#BFDBFE',
    icon: ArrowDown,
    badgeBg: '#DBEAFE',
    badgeText: '#1E40AF',
  },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  in_progress: { label: 'In Progress', bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
  complete: { label: 'Complete', bg: '#D1FAE5', text: '#065F46', border: '#A7F3D0' },
};

// ── Helpers ──

const formatDueDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);
  const dayAfterTomorrow = new Date(todayStart);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);

  if (d >= todayStart && d < tomorrowStart) return 'Today';
  if (d >= tomorrowStart && d < dayAfterTomorrow) return 'Tomorrow';
  if (d < todayStart) return { text: d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }), overdue: true };
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

// ── Status Badge ──

const StatusBadge = ({ status, onClick }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold transition-all hover:opacity-80"
      style={{ backgroundColor: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
    >
      {cfg.label}
    </button>
  );
};

// ── Inline Add Form ──

const InlineAddForm = ({ priority, onAdd, onCancel, isAdmin }) => {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [shared, setShared] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onAdd({ title: title.trim(), priority, due_date: dueDate || null, shared_with_admins: shared });
    setTitle('');
    setDueDate('');
    setShared(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-3 px-5 py-3 bg-gray-50/80 border-t border-gray-100" onKeyDown={handleKeyDown}>
      <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
      <input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Type a task and press Enter..."
        className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none"
      />
      {isAdmin && (
        <button
          type="button"
          onClick={() => setShared(!shared)}
          className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition ${
            shared
              ? 'bg-violet-50 border-violet-300 text-violet-700'
              : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600'
          }`}
          title="Share with company admins"
        >
          <Share2 className="w-3 h-3" />
          {shared ? 'Shared' : 'Share'}
        </button>
      )}
      <input
        type="date"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        className="text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-400"
      />
      <button
        type="submit"
        disabled={!title.trim()}
        className="text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg px-3 py-1.5 transition disabled:opacity-40"
      >
        Add
      </button>
      <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
        <X className="w-4 h-4" />
      </button>
    </form>
  );
};

// ── Edit Modal ──

const EditModal = ({ todo, onSave, onClose, isAdmin }) => {
  const [title, setTitle] = useState(todo.title);
  const [priority, setPriority] = useState(todo.priority);
  const [status, setStatus] = useState(todo.status);
  const [dueDate, setDueDate] = useState(
    todo.due_date ? new Date(todo.due_date).toISOString().split('T')[0] : ''
  );
  const [notes, setNotes] = useState(todo.notes || '');
  const [shared, setShared] = useState(todo.shared_with_admins || false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      priority,
      status,
      due_date: dueDate || '',
      notes,
      shared_with_admins: shared,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Pencil className="w-4 h-4 text-gray-400" />
            Edit Task
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Priority + Status row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="complete">Complete</option>
              </select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          </div>

          {/* Share with admins */}
          {isAdmin && !todo.is_shared && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shared}
                  onChange={(e) => setShared(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm text-gray-700 flex items-center gap-1.5">
                  <Share2 className="w-3.5 h-3.5 text-violet-500" />
                  Share with company admins
                </span>
              </label>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Todo Row ──

const TodoRow = ({ todo, onToggle, onStatusChange, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isComplete = todo.status === 'complete';

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const due = formatDueDate(todo.due_date);
  const dueLabel = (due && typeof due === 'object') ? due.text : due;
  const overdue = (due && typeof due === 'object') && due.overdue && !isComplete;

  const cycleStatus = () => {
    const order = ['pending', 'in_progress', 'complete'];
    const next = order[(order.indexOf(todo.status) + 1) % order.length];
    onStatusChange(todo._id, next);
  };

  return (
    <div className={`group flex items-center gap-3 px-5 py-3 transition-colors hover:bg-gray-50/80 ${isComplete ? 'opacity-60' : ''}`}>
      {/* Check circle */}
      <button
        onClick={() => onToggle(todo._id)}
        className="flex-shrink-0 transition-transform hover:scale-110"
      >
        {isComplete ? (
          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#10B981' }}>
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </div>
        ) : (
          <Circle className="w-5 h-5 text-gray-300 hover:text-gray-400" />
        )}
      </button>

      {/* Title */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className={`text-sm truncate ${isComplete ? 'line-through text-gray-400' : 'text-gray-800'}`}>
          {todo.title}
        </span>
        {todo.shared_with_admins && !todo.is_shared && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 text-[10px] font-medium flex-shrink-0">
            <Share2 className="w-2.5 h-2.5" /> Shared
          </span>
        )}
        {todo.is_shared && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-medium flex-shrink-0">
            <Users className="w-2.5 h-2.5" /> {todo.shared_by || 'Admin'}
          </span>
        )}
      </div>

      {/* Due date */}
      <span className={`text-xs flex-shrink-0 min-w-[60px] text-right flex items-center gap-1 justify-end ${overdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
        {overdue && <AlertTriangle className="w-3 h-3" />}
        {dueLabel || '-'}
      </span>

      {/* Status */}
      <div className="flex-shrink-0 min-w-[90px] flex justify-center">
        <StatusBadge status={todo.status} onClick={cycleStatus} />
      </div>

      {/* Actions */}
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-1 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-all"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-8 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20 w-36">
            <button
              onClick={() => { setMenuOpen(false); onEdit(todo); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              onClick={() => { setMenuOpen(false); onDelete(todo._id); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Priority Group ──

const PriorityGroup = ({ priority, todos, onToggle, onStatusChange, onEdit, onDelete, onAdd, isAdmin }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const cfg = PRIORITY_CONFIG[priority];
  const PriorityIcon = cfg.icon;

  const completedCount = todos.filter(t => t.status === 'complete').length;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Group header */}
      <div
        className="flex items-center justify-between px-5 py-3 cursor-pointer select-none hover:bg-gray-50/50 transition"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2.5">
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
          <PriorityIcon className="w-4 h-4" style={{ color: cfg.color }} />
          <h3 className="text-sm font-semibold text-gray-800">{cfg.label}</h3>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{ backgroundColor: cfg.badgeBg, color: cfg.badgeText }}
          >
            {completedCount}/{todos.length}
          </span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setShowAddForm(true); }}
          className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Tasks */}
      {!collapsed && (
        <div className="border-t border-gray-100">
          {todos.length === 0 && !showAddForm ? (
            <div className="px-5 py-6 text-center">
              <p className="text-sm text-gray-400">No tasks yet</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-3 h-3" /> Add a task
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {todos.map((todo) => (
                <TodoRow
                  key={todo._id}
                  todo={todo}
                  onToggle={onToggle}
                  onStatusChange={onStatusChange}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}

          {/* Inline add form */}
          {showAddForm && (
            <InlineAddForm
              priority={priority}
              isAdmin={isAdmin}
              onAdd={(data) => { onAdd(data); setShowAddForm(false); }}
              onCancel={() => setShowAddForm(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Page ──

const MyTodos = () => {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTodo, setEditingTodo] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, complete

  const isAdmin = user?.role === 'company_admin' || user?.role === 'admin' || user?.role === 'main_admin';

  usePageTitle('My Tasks');

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      const res = await api.get('/todos');
      setTodos(res.data);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (data) => {
    try {
      const res = await api.post('/todos', data);
      setTodos((prev) => [...prev, res.data]);
      toast.success('Task added');
    } catch (err) {
      toast.error('Failed to add task');
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.post(`/todos/${id}/toggle`);
      setTodos((prev) => prev.map((t) => (t._id === id ? res.data : t)));
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await api.put(`/todos/${id}`, { status });
      setTodos((prev) => prev.map((t) => (t._id === id ? res.data : t)));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleEdit = async (updates) => {
    if (!editingTodo) return;
    try {
      const res = await api.put(`/todos/${editingTodo._id}`, updates);
      setTodos((prev) => prev.map((t) => (t._id === editingTodo._id ? res.data : t)));
      setEditingTodo(null);
      toast.success('Task updated');
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/todos/${id}`);
      setTodos((prev) => prev.filter((t) => t._id !== id));
      toast.success('Task deleted');
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  // Filter and group
  const filtered = todos.filter((t) => {
    if (filter === 'active') return t.status !== 'complete';
    if (filter === 'complete') return t.status === 'complete';
    return true;
  });

  const grouped = {
    high: filtered.filter((t) => t.priority === 'high'),
    medium: filtered.filter((t) => t.priority === 'medium'),
    low: filtered.filter((t) => t.priority === 'low'),
  };

  const totalCount = todos.length;
  const completedCount = todos.filter((t) => t.status === 'complete').length;
  const overdueCount = todos.filter((t) => {
    if (!t.due_date || t.status === 'complete') return false;
    return new Date(t.due_date) < new Date(new Date().toDateString());
  }).length;

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-blue-600" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
              <ListTodo className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">My Tasks</h1>
              <p className="text-sm text-gray-500">
                {completedCount}/{totalCount} completed
                {overdueCount > 0 && (
                  <span className="text-red-500 ml-2">
                    · {overdueCount} overdue
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5">
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active' },
              { key: 'complete', label: 'Completed' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  filter === tab.key
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="rounded-xl bg-white border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{totalCount}</p>
          </div>
          <div className="rounded-xl bg-white border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" /> In Progress
            </p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {todos.filter(t => t.status === 'in_progress').length}
            </p>
          </div>
          <div className="rounded-xl bg-white border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
              <Check className="w-3 h-3" /> Completed
            </p>
            <p className="text-2xl font-bold text-green-600 mt-1">{completedCount}</p>
          </div>
          <div className="rounded-xl bg-white border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Overdue
            </p>
            <p className={`text-2xl font-bold mt-1 ${overdueCount > 0 ? 'text-red-500' : 'text-gray-300'}`}>{overdueCount}</p>
          </div>
        </div>

        {/* Column headers */}
        <div className="flex items-center gap-3 px-5 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
          <span className="w-5" />
          <span className="flex-1">Task</span>
          <span className="min-w-[60px] text-right flex items-center gap-1 justify-end">
            <Calendar className="w-3 h-3" /> Date
          </span>
          <span className="min-w-[90px] text-center">Status</span>
          <span className="w-6" />
        </div>

        {/* Priority groups */}
        <div className="space-y-4">
          {['high', 'medium', 'low'].map((priority) => (
            <PriorityGroup
              key={priority}
              priority={priority}
              todos={grouped[priority]}
              isAdmin={isAdmin}
              onToggle={handleToggle}
              onStatusChange={handleStatusChange}
              onEdit={(todo) => setEditingTodo(todo)}
              onDelete={handleDelete}
              onAdd={handleAdd}
            />
          ))}
        </div>

        {/* Empty state */}
        {totalCount === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
              <ListTodo className="w-8 h-8 text-violet-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">No tasks yet</h3>
            <p className="text-sm text-gray-500 mb-4">Click the + button in any priority group to add your first task.</p>
          </div>
        )}

        {/* Edit modal */}
        {editingTodo && (
          <EditModal
            todo={editingTodo}
            isAdmin={isAdmin}
            onSave={handleEdit}
            onClose={() => setEditingTodo(null)}
          />
        )}
      </div>
    </Sidebar>
  );
};

export default MyTodos;
